import { Directive, ElementRef, HostListener, forwardRef, Input } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

@Directive({
  selector: "input[numbersOnly]",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumbersOnlyDirective),
      multi: true,
    },
  ],
})
export class NumbersOnlyDirective implements ControlValueAccessor {
  @Input() step = 1;
  @Input() min: number | null = null;
  @Input() max: number | null = null;

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private el: ElementRef<HTMLInputElement>) {}

  writeValue(value: number | null): void {
    this.el.nativeElement.value = value === null || value === undefined ? "" : value.toString();
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // 🔢 Typing logic
  @HostListener("input", ["$event"])
  onInputChange(event: Event) {
    const input = event.target as HTMLInputElement;

    let value = input.value.replace(/[^0-9.]/g, "");

    // Allow only one decimal point
    const parts = value.split(".");
    if (parts.length > 2) {
      value = parts[0] + "." + parts.slice(1).join("");
    }

    input.value = value;

    this.emitClampedValue(value);
  }

  // ⬆⬇ Arrow keys
  @HostListener("keydown", ["$event"])
  onKeyDown(event: KeyboardEvent) {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
      return;
    }

    event.preventDefault();

    const current = this.el.nativeElement.value === "" ? 0 : Number(this.el.nativeElement.value);

    const delta = event.key === "ArrowUp" ? this.step : -this.step;
    const next = this.clamp(current + delta);

    this.setValue(next);
  }

  @HostListener("blur")
  onBlur() {
    this.onTouched();
  }

  // Helpers
  private emitClampedValue(value: string) {
    if (value === "") {
      this.onChange(null);
      return;
    }

    const num = Number(value);
    if (isNaN(num)) {
      this.onChange(null);
      return;
    }

    const clamped = this.clamp(num);

    // Update view if clamped
    if (clamped !== num) {
      this.el.nativeElement.value = clamped.toString();
    }

    this.onChange(clamped);
  }

  private setValue(value: number) {
    const clamped = this.clamp(value);
    this.el.nativeElement.value = clamped.toString();
    this.onChange(clamped);
  }

  private clamp(value: number): number {
    if (this.min !== null && value < this.min) {
      return this.min;
    }
    if (this.max !== null && value > this.max) {
      return this.max;
    }
    return value;
  }
}
